import { RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
// eslint-disable-next-line arkestro-local/no-react-hot-renderer -- pending native conversion of the NSB cells
import { HotRendererProps, HotTableRef } from '@handsontable/react-wrapper';
import Handsontable from 'handsontable';
import useTranslation from 'next-translate/useTranslation';
import { getColIndex } from '@components/bid_request/details/utils/getColIndex';
import { fetchSmartBaselineFlavorsByItemName, NsbFlavorsResponse } from '@api/SmartBaselineFlavorsApi';
import { NsbSearchItem } from '@api/SmartBaselineSearchApi';
import { appliedNameBySbKey } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/appliedNameStore';
import { appliedSmartBaselineKeys } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/appliedSmartBaselineStore';
import {
  registerBenchmarkRefresh,
  unregisterBenchmarkRefresh,
} from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/benchmarkRefreshStore';
import { getNegotiationColIdx, getPriceColIdx } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/columnIndexHelpers';
import { LineItemsContext } from '@providers/LineItemsProvider/LineItemsContext';
import { UserContext } from '@providers/UserProvider/UserProvider';
import { DEFAULT_PRECISION, displayCurrencyWithPrecision } from '@utils/currency';
import { NO_DATA_DISPLAY } from '@utils/data';
import { LineItemTableColumnSettings } from '@components/bid_request/details/types';
import { ID } from 'types';
// eslint-disable-next-line import/no-cycle -- runtime-only (handler) usage; QuickPicker <-> BenchmarkRenderer cycle is intentional (co-located, never crosses a PR boundary)
import { showSmartBaselineQuickPicker } from '@components/bid_request/details/LineItemsTable/SmartBaselineQuickPicker';
// Leaf imports, not the @features/nsb-build-baseline barrel: the barrel re-exports
// build-baseline-flow-container.tsx, so importing it here would route this file through the whole
// feature's surface for 2 narrow symbols -- needlessly fragile, even though nothing in that file
// imports back from here directly today (a separate fix already removed that edge).
import { parseBuiltBaselineDetails } from '@features/nsb-build-baseline/built-baseline-details';
import { showBuiltBaselineDetailsModal } from '@features/nsb-build-baseline/built-baseline-details-store';
import {
  getResolvedItemNameToUse,
  getResolvedNameForLineItem,
  subscribeResolvedItemNameToUse,
  subscribeResolvedNameForLineItem,
} from './nsbResolvedItemBridge';
import { registerNsbEventResetter } from './nsbEventReset';
import { getCachedFlavors, hasCachedFlavors, setCachedFlavors, subscribeFlavorsCache } from './nsbFlavorsCache';
import { isIcoFallback, markIcoFallback } from './nsbIcoFallbackStore';
import { getNsbRowConfig, registerNsbRowHydrator, setNsbRowConfigColumn } from './nsbRowConfigStore';
import { invalidateRow, markSatisfiedFromStorage, needsRecompute } from './nsbRecomputeGate';
import { isUserProvided } from './userProvidedStore';
import { UserProvidedBaselineCell } from './UserProvidedBaselineCell';
import { recordMirroredBaseline, seedFromFlavor } from './nsbPriceSeed';
import { parseCellNumber } from './parseCellNumber';
import {
  INTELLIGENT_COUNTER_OFFER,
  MANUAL_ENTRY,
  MIRROR_STARTING_PRICE,
  getBaselineSelection,
  getStartingPriceSelection,
  subscribeNsbSelection,
} from './nsbSelectionStore';
import { noteNsbCellPainted, noteNsbFetchFinished, noteNsbFetchStarted } from './nsbApplyStore';
import { isOverrideAllowed, subscribeLotOverride } from './nsbOverrideStore';
import {
  BUILT_BASELINE_LABEL_KEY,
  FLAVOR_DEFS,
  ICO_LABEL_KEY,
  MANUAL_ENTRY_LABEL_KEY,
  translateFlavorLabel,
} from './flavorDefs';
import styles from './NsbPriceCell.module.scss';

type HotInstance = Handsontable.Core;

// True when the cell at (row, colIdx) already holds `target` numerically. Used to make the
// smart-baseline price/negotiation seeds IDEMPOTENT: a cell value round-trips through the save
// pipeline as a string ("5"), so `"5" !== 5` would re-write forever. Normalize both sides and compare.
const isCellValueEqual = (hot: HotInstance | null | undefined, row: number, colIdx: number, target: number): boolean => {
  if (!hot || colIdx < 0) return false;
  const current = hot.getDataAtCell(row, colIdx);
  if (current === '' || current == null) return false;
  return parseCellNumber(current) === target;
};

// Column index for the line-item name column (id === 'name').
export const getNameColIdx = (columnSettings: LineItemTableColumnSettings[]): number => (
  getColIndex({ columnSettings, headerId: 'name' })
);

// Column index for the hidden line-item id column (id === 'id').
export const getIdColIdx = (columnSettings: LineItemTableColumnSettings[]): number => (
  getColIndex({ columnSettings, headerId: 'id' })
);

// Reads the STABLE line-item id for a row (the DB id, or the client `new-<uuid>` assigned to
// freshly-imported/added rows before save). This is the per-row identity used to key NSB state —
// the Handsontable row index is reused across re-uploads and reordering and must NOT be the key.
export const getLineItemId = (columnSettings: LineItemTableColumnSettings[], hot: HotInstance | null | undefined, row: number | undefined): string | null => {
  if (!hot || row === undefined) return null;
  const idColIdx = getIdColIdx(columnSettings);
  if (idColIdx < 0) return null;
  const id = hot.getDataAtCell(row, idColIdx);
  return id != null && id !== '' ? String(id) : null;
};

// ─── Module-level state for SmartBaselineIntelModal ───────────────────────────

export interface IntelModalState {
  column: 'baseline' | 'negotiation';
  columnLabel: string;
  columnSettings: LineItemTableColumnSettings[];
  // The event's live currency, carried from the grid cell that opened the picker so the popup and the
  // full modal price their flavors the same way the cell does.
  currency: string;
  flavors: NsbFlavorsResponse;
  hotRef: RefObject<HotTableRef>;
  lotId: ID;
  nsbItem: NsbSearchItem;
  row: number;
  sbKey: string;
}

let globalIntelModalSetter: ((state: IntelModalState | null) => void) | null = null;

export const registerIntelModalSetter = (fn: ((state: IntelModalState | null) => void) | null): void => {
  globalIntelModalSetter = fn;
};

export const showSmartBaselineIntelModal = (state: IntelModalState): void => {
  globalIntelModalSetter?.(state);
};

export const hideSmartBaselineIntelModal = (): void => {
  globalIntelModalSetter?.(null);
};

// The by-name flavors cache lives in ./nsbFlavorsCache (a leaf module with subscribe/notify) so a
// cell re-renders the instant its flavors land — see the imports above.

// Tracks sbKeys where recommended price has already been auto-applied, so remounts don't re-trigger.
const autoAppliedSbKeys = new Set<string>();

// ─── Lot-level default flavor state ──────────────────────────────────────────

// lotId → 'recommended' | 'F1' | 'F2' | ... | 'F8'
export const lotDefaultFlavorStore = new Map<ID, string>();

// sbKeys where user explicitly applied a flavor via modal — lot default won't override these
export const userSelectedFlavorKeys = new Set<string>();

// On every event change, clear this module's per-row applied-state so a new event (or a re-entry)
// re-derives each row from scratch and matches a first-time load exactly. flavorsCache is NOT
// cleared on purpose — it is keyed by item name and is the same for the same item across events.
registerNsbEventResetter(() => {
  autoAppliedSbKeys.clear();
  appliedNameBySbKey.clear();
  userSelectedFlavorKeys.clear();
  lotDefaultFlavorStore.clear();
  appliedSmartBaselineKeys.clear();
});

/**
 * The persisted source is not always a flavor id, and everything that isn't one needs its own case:
 * the FLAVOR_DEFS lookup misses, and the fallback credits the buyer with "Manual Entry".
 *
 * A Built Baseline commit persists a JSON blob (see nsb-build-baseline/built-baseline-details.ts).
 * The buyer's own entry persists MANUAL_ENTRY. INTELLIGENT_COUNTER_OFFER is written by the BACKEND
 * after round 1 rather than by anything in this module — mislabelling it
 * attributes a price the system derived to the buyer, on a row they never touched.
 *
 * NOTE: that last sentence is only true while ICO is backend-only. Once a buyer can choose ICO for
 * an individual line item, this module writes it too and the row is no longer one they never
 * touched. The case itself stays required either way — only the reason for it narrows, so revisit
 * the wording rather than the branch.
 *
 * MIRROR_STARTING_PRICE is deliberately absent: it describes the baseline tracking this column, so
 * it is only ever stored against the baseline price, never against the negotiation price.
 */
const resolveNegotiationLabel = (negotiationSource: string): string => {
  if (parseBuiltBaselineDetails(negotiationSource)) return BUILT_BASELINE_LABEL_KEY;
  if (negotiationSource === MANUAL_ENTRY) return MANUAL_ENTRY_LABEL_KEY;
  if (negotiationSource === INTELLIGENT_COUNTER_OFFER) return ICO_LABEL_KEY;
  return FLAVOR_DEFS.find(f => f.id === negotiationSource)?.label ?? MANUAL_ENTRY_LABEL_KEY;
};

// Rebuild the Negotiation column's applied-state from a row's persisted nsb_row_config on load. Marking
// the row userSelected makes the auto-apply effect skip it (its saved price already sits in the cell),
// and the applied meta restores the flavor/Manual-Entry label the buyer left it on.
registerNsbRowHydrator(entries => {
  entries.forEach(({ baselinePrice, buyerPicked, config, icoFallback, negotiationPrice, sbKey }) => {
    if (!config.negotiation) return;
    // The backend's ICO fallback on an unpriced row: leave it unsettled so the seeder can replace it
    // with a smart baseline match. A row that never matches keeps the fallback and ICO fills it.
    if (icoFallback) {
      markIcoFallback(sbKey);
      return;
    }
    const label = resolveNegotiationLabel(config.negotiation);
    markSatisfiedFromStorage(sbKey);
    // In the non-split / Mirror layout the Negotiation column (`nsb_benchmark`) is read-only and never
    // persists, so `negotiation_starting_price` comes back null; the buyer's applied price lives in the
    // saved baseline (`price`). Prefer the negotiation price when present, else fall back to baseline so
    // a reload restores the real value instead of 0 (which left the row flashing $0.00 in a mirror loop).
    const negNum = Number(negotiationPrice);
    const price = negotiationPrice != null && negotiationPrice !== '' && !Number.isNaN(negNum) && negNum > 0
      ? negNum
      : Number(baselinePrice);
    // A default-following row must stay unmarked, or the event-level dropdown would move nothing.
    if (buyerPicked) userSelectedFlavorKeys.add(sbKey);
    appliedSmartBaselineKeys.set(sbKey, { confScore: 0, flavorLabel: label, price });
  });
});

// Per-lot re-apply: when lot default changes, each renderer clears its autoApplied flag and re-applies
const lotReapplyCallbacks = new Map<ID, Set<() => void>>();
export const triggerLotReapply = (lotId: ID): void => { lotReapplyCallbacks.get(lotId)?.forEach(fn => fn()); };
const addLotReapplyCallback = (lotId: ID, fn: () => void): void => {
  if (!lotReapplyCallbacks.has(lotId)) lotReapplyCallbacks.set(lotId, new Set());
  lotReapplyCallbacks.get(lotId)!.add(fn);
};
const removeLotReapplyCallback = (lotId: ID, fn: () => void): void => {
  lotReapplyCallbacks.get(lotId)?.delete(fn);
};

// ─── SmartBaselineBenchmarkRenderer ──────────────────────────────────────────

interface SmartBaselineBenchmarkRendererProps extends Partial<HotRendererProps> {
  // A getter (not the array): the element lives inside `columns`, so holding the array on props would
// make `columns[i].component.props.columnSettings === columns` — a self-reference that sends
// Handsontable's cycle-detection-free settings deep-compare into infinite recursion. Calling the
// getter yields the same live array without storing it on the settings graph.
  columnSettings: () => LineItemTableColumnSettings[];
  // The event's LIVE currency, threaded from generateTableConfiguration — the same value the
  // native price columns and SuggestedPriceCellRenderer format with. Deliberately NOT read from
  // LineItemsContext's `request`: that is the static server prop, while the grid's currency is
  // reducer state the header's currency selector mutates (CURRENCY_CHANGE), so the two diverge.
  currency: string;
  hotRef: RefObject<HotTableRef>;
  lotId: ID;
}

export const SmartBaselineBenchmarkRenderer = ({
  columnSettings: getColumnSettings,
  currency,
  row,
  hotRef,
  lotId,
  value,
}: SmartBaselineBenchmarkRendererProps) => {
  const columnSettings = getColumnSettings();
  // Read the org's award precision from config rather than taking it as a prop: a prop with a default
  // would silently fall back to 2 wherever a mount point forgot to pass it — which is the exact bug
  // this column had. Same access pattern as PriceCell and the awarding components.
  const { company } = useContext(UserContext);
  const { award_price_precision: pricePrecision = DEFAULT_PRECISION } = company || {};
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reapplyKey, setReapplyKey] = useState(0);
  const fetchedKeyRef = useRef<string | null>(null);

  // Lets the match toast tell when the NSB prices have stopped appearing. No dep array on purpose:
  // every render of every cell counts, and filling a large event takes seconds.
  useEffect(() => { noteNsbCellPainted(); });

  useEffect(() => {
    if (row === undefined) return undefined;
    registerBenchmarkRefresh(row, () => setRefreshKey(k => k + 1));
    return () => unregisterBenchmarkRefresh(row);
  }, [row]);

  const { request } = useContext(LineItemsContext);
  // Key per-row NSB state by the STABLE line-item id (event-scoped), never by the row index — row
  // indices are reused on re-upload/reorder and would make a new item inherit the old row's state.
  const lineItemId = getLineItemId(columnSettings, hotRef.current?.hotInstance, row);
  const sbKey = lineItemId && request?.id ? `${request.id}_${lineItemId}` : null;

  // v2 Part 1 -> NSB handoff: the line-item name is the bridge key; Part 1 (item match/intel)
  // resolves it to an item_name_to_use, and NSB joins the precompute table directly on that value
  // (service side: lookup_type = name_sku, company_id from session, and the composed precompute key
  // item_name + '-' + item_id + ' - ' + unit_of_measure = item_name_to_use). The old poc path read
  // rowSelectionMetadata, which nothing populates in v2.
  const nameColIdx = getNameColIdx(columnSettings);
  const lineItemName: string = (nameColIdx >= 0 && row !== undefined ? (hotRef.current?.hotInstance?.getDataAtCell(row, nameColIdx) as string) : '') || '';

  // Re-render whenever Part 1 (asynchronously) resolves this row's item — single match or user pick.
  // useSyncExternalStore re-reads the snapshot AFTER subscribing, so a resolution that lands in the
  // gap between this render and the subscription can't be missed. The previous useEffect-subscribe
  // could miss that notification and leave the cell permanently blank on a match that succeeded.
  const subscribeResolvedByName = useCallback(
    (cb: () => void): (() => void) => {
      if (!lineItemName) return () => undefined;
      return subscribeResolvedItemNameToUse(lineItemName, cb);
    },
    [lineItemName],
  );
  const nameResolved = useSyncExternalStore(
    subscribeResolvedByName,
    () => (lineItemName ? getResolvedItemNameToUse(lineItemName) : undefined),
    () => undefined,
  );

  // Also subscribe to the DURABLE per-row resolution (keyed by the stable line-item id). This is the
  // channel a buyer's pick sets directly (and that hydration seeds on reload), so the benchmark reacts
  // to a pick even though the Line Item Name cell is no longer renamed. Prefer it over the name-keyed
  // in-session resolution, and fall back to the name bridge for fresh auto-resolves.
  const subscribeResolvedById = useCallback(
    (cb: () => void): (() => void) => (lineItemId ? subscribeResolvedNameForLineItem(lineItemId, cb) : () => undefined),
    [lineItemId],
  );
  const idResolved = useSyncExternalStore(
    subscribeResolvedById,
    () => (lineItemId ? getResolvedNameForLineItem(lineItemId) : undefined),
    () => undefined,
  );

  const resolvedItemName = idResolved ?? nameResolved;

  // When this row's item actually CHANGES (upload / rename) — detected by comparing the current name
  // to the name we previously seeded here — clear the stale per-row seed guard so the new item
  // re-seeds. Guarded on a genuine change (not every mount) so a harmless remount doesn't loop.
  useEffect(() => {
    if (!sbKey || !lineItemName) return;
    const seededName = appliedNameBySbKey.get(sbKey);
    if (seededName !== undefined && seededName !== lineItemName) {
      autoAppliedSbKeys.delete(sbKey);
      userSelectedFlavorKeys.delete(sbKey);
      appliedSmartBaselineKeys.delete(sbKey);
      appliedNameBySbKey.delete(sbKey);
      setReapplyKey(k => k + 1);
    }
  }, [lineItemName, sbKey]);

  // Minimal NsbSearchItem built from the resolved item_name_to_use — carries the join value and the
  // label the Intel modal renders. (The deep-dive data itself comes from the by-name flavors fetch.)
  const nsbItem = useMemo<NsbSearchItem | undefined>(() => (resolvedItemName ? {
    item_id: '',
    item_id_cleaned: '',
    item_name: resolvedItemName,
    item_name_cleaned: resolvedItemName,
    label: resolvedItemName,
    lookup_type: 'name_sku',
    unit_of_measure: '',
    unit_of_measure_raw: '',
  } : undefined), [resolvedItemName]);

  const cacheKey = resolvedItemName ? `byname__${resolvedItemName}` : null;

  // Reactively read this row's flavors from the shared cache. useSyncExternalStore re-renders the
  // cell the instant its result is cached by ANY instance's fetch, so a cold load fills in
  // progressively instead of leaving rows blank until a remount. (Same pattern as the name bridge.)
  const flavors = useSyncExternalStore(
    useCallback((cb: () => void) => subscribeFlavorsCache(cacheKey, cb), [cacheKey]),
    () => getCachedFlavors(cacheKey),
    () => null,
  );

  // Register lot re-apply callback — when lot default changes, clear autoApplied and re-apply
  useEffect(() => {
    if (!sbKey || !lotId) return undefined;
    const fn = () => {
      if (!userSelectedFlavorKeys.has(sbKey)) {
        autoAppliedSbKeys.delete(sbKey);
        setReapplyKey(k => k + 1);
      }
    };
    addLotReapplyCallback(lotId, fn);
    return () => removeLotReapplyCallback(lotId, fn);
  }, [sbKey, lotId]);

  // Revert this row to the default when the lot's negotiation-override toggle turns OFF.
  useEffect(() => subscribeLotOverride(() => {
    if (!sbKey || !lotId) return;
    if (!isOverrideAllowed(lotId, 'negotiation') && userSelectedFlavorKeys.has(sbKey)) {
      userSelectedFlavorKeys.delete(sbKey);
      autoAppliedSbKeys.delete(sbKey);
      setReapplyKey(k => k + 1);
    }
  }), [sbKey, lotId]);

  // Re-apply when the event-level Starting/Baseline selection changes (skip user-overridden rows).
  useEffect(() => subscribeNsbSelection(() => {
    if (sbKey && !userSelectedFlavorKeys.has(sbKey)) {
      autoAppliedSbKeys.delete(sbKey);
      invalidateRow(sbKey);
      setReapplyKey(k => k + 1);
    }
  }), [sbKey]);

  useEffect(() => {
    if (!resolvedItemName || !sbKey || !cacheKey) return;
    if (hasCachedFlavors(cacheKey)) return; // already cached — the reactive read above shows it
    // A cache eviction (the toolbar Refresh button forcing a fresh price, via evictCachedFlavors in
    // MatchProgressToast) clears the entry out from under an already-mounted cell: `flavors` flips
    // falsy, but `fetchedKeyRef` still remembers the OLD fetch for this exact cacheKey as "done" and
    // would otherwise block a real refetch forever -- this component never unmounts on withhold/apply
    // the way the split-layout columns do, so there's no fresh `fetchedKeyRef` to rely on. Clearing it
    // here, gated on `!flavors`, only fires post-eviction: while a fetch is genuinely still in flight
    // `flavors` hasn't changed (still null from before the fetch started), so this can't create a
    // second concurrent request for the same key.
    if (fetchedKeyRef.current === cacheKey && !flavors) fetchedKeyRef.current = null;
    if (fetchedKeyRef.current === cacheKey) return; // in-flight for this key
    fetchedKeyRef.current = cacheKey;

    setLoading(true);
    // Registered around the fetch, not around the render: this is the work the toast waits on before it
    // reports the prices are in.
    noteNsbFetchStarted();
    fetchSmartBaselineFlavorsByItemName(resolvedItemName)
      .then((result: NsbFlavorsResponse) => {
        if (result?.precomputes) {
          // Cache + notify: every subscribed cell for this item re-renders and shows its baseline.
          setCachedFlavors(cacheKey, result);
        } else if (fetchedKeyRef.current === cacheKey) {
          // Nothing came back (failed / empty) — release the in-flight marker so a later render retries.
          fetchedKeyRef.current = null;
        }
      })
      .catch(() => {
        if (fetchedKeyRef.current === cacheKey) fetchedKeyRef.current = null;
      })
      .finally(() => {
        noteNsbFetchFinished();
        setLoading(false);
      });
  }, [cacheKey, flavors]);

  // Auto-apply the lot-default flavor (falls back to recommended if not available for this item)
  useEffect(() => {
    if (!flavors?.precomputes || !sbKey || row === undefined) return;
    // NSB #3/#7: the buyer uploaded this row's price. Never seed a smart baseline over it. Reading the
    // write-once flag makes this idempotent — re-seed triggers (dropdown/override changes) fire harmlessly.
    if (isUserProvided(sbKey)) return;
    // A hydrated default-following row has neither userSelectedFlavorKeys nor autoAppliedSbKeys set, so
    // without this a cache fill for another row sharing this resolved name would re-derive and overwrite
    // its saved price.
    if (!needsRecompute(sbKey)) return;
    if (userSelectedFlavorKeys.has(sbKey)) return;
    // Never seed over a manual entry. The Baseline column's seeder already had this guard; the
    // Negotiation column relied only on userSelectedFlavorKeys, which a value TYPED INTO THE CELL is
    // never added to — so changing any event-level option re-seeded the typed value away.
    if (appliedSmartBaselineKeys.get(sbKey)?.flavorLabel === MANUAL_ENTRY_LABEL_KEY) return;
    // Re-seed if the cell lost its value (e.g. on reload the saved price didn't make it back into the
    // cell): skip ONLY when already applied AND the cell still holds a value. An empty matched cell is
    // editable (the lock needs a value), so this seed always lands, gives the cell a real value, and it
    // then locks + saves cleanly. Once the value is present the guard skips — so it cannot loop.
    const hasSeededValue = value !== '' && value != null && !Number.isNaN(parseCellNumber(value));
    if (autoAppliedSbKeys.has(sbKey) && hasSeededValue) return;

    const prec = flavors.precomputes;
    const lotDefault = lotDefaultFlavorStore.get(lotId) ?? getStartingPriceSelection();
    // Manual Entry — the buyer sets the starting price per line item; do not seed a smart-baseline price.
    if (lotDefault === MANUAL_ENTRY || lotDefault === INTELLIGENT_COUNTER_OFFER) return;
    // Records the flavour as the row's auto source too; the id comes from the resolver, not a label lookup.
    const { label: flavorLabel, price } = seedFromFlavor('negotiation', prec, lotDefault, sbKey, lotId);

    if (price == null) return;

    const confScore = prec.recommended_conf_score ?? 0;
    autoAppliedSbKeys.add(sbKey);
    appliedNameBySbKey.set(sbKey, lineItemName); // remember WHICH item we seeded here (upload detection)
    // Negotiation Starting Price applied for this row (the Baseline column's mirror reads this).
    appliedSmartBaselineKeys.set(sbKey, { confScore, flavorLabel, price });

    // Seed the editable Negotiation Starting Price column so its cell holds the applied price — but
    // only if it isn't already that value. The cell renderer recycles on every grid redraw (the
    // component fully unmounts/remounts), so per-mount state can't dedupe these writes; compare
    // against the live cell instead. An unconditional write would dispatch a change → rebuild lots →
    // remount this cell → write again, looping forever (~8x/sec) and making links unclickable.
    const hotInstance = hotRef.current?.hotInstance;
    const negColIdx = getNegotiationColIdx(columnSettings);
    if (negColIdx >= 0 && !isCellValueEqual(hotInstance, row, negColIdx, price)) {
      hotInstance?.setDataAtCell(row, negColIdx, price);
    }

    // Mirror Negotiations: the Negotiation column is read-only under Smart Baseline, so its seed
    // doesn't reliably re-pass `value` to trigger the value-driven mirror sync. Seed the Baseline
    // (price) cell directly here with the same price so the baseline + Total populate. Same idempotency
    // guard as above — skip when the cell already holds this price so the seed can't drive a re-render loop.
    if (getBaselineSelection() === MIRROR_STARTING_PRICE) {
      const priceColIdx = getPriceColIdx(columnSettings);
      if (priceColIdx >= 0) recordMirroredBaseline(sbKey, lotId);
      if (priceColIdx >= 0 && !isCellValueEqual(hotInstance, row, priceColIdx, price)) {
        hotInstance?.setDataAtCell(row, priceColIdx, price);
      }
    }
  }, [flavors, sbKey, row, hotRef, lotId, reapplyKey, value, columnSettings]);

  // Mirror Negotiations: keep this row's Baseline (price) cell in sync with the negotiation's
  // EFFECTIVE price. Use the smart-applied price when there is one (reliable — does NOT depend on the
  // read-only negotiation cell's `value` having settled, which is what left BP blank on a fresh load);
  // otherwise mirror the manually-typed negotiation cell. Agrees with the direct seed in auto-apply,
  // so the two writers never fight.
  useEffect(() => {
    if (getBaselineSelection() !== MIRROR_STARTING_PRICE || row === undefined) return;
    // NSB #3/#7: never mirror-write a user-provided row's baseline cell — its BPU already holds the
    // uploaded price and is rendered by the User-Provided branch. (Not the flicker cause — that's a
    // pre-existing warm-cache issue in base, tracked separately.)
    if (sbKey && isUserProvided(sbKey)) return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const priceColIdx = getPriceColIdx(columnSettings);
    if (priceColIdx < 0) return;
    const applied = sbKey ? appliedSmartBaselineKeys.get(sbKey) : undefined;
    let target: number | '' = '';
    if (applied) {
      target = applied.price;
    } else {
      const negColIdx = getNegotiationColIdx(columnSettings);
      const negRaw = negColIdx >= 0 ? hot.getDataAtCell(row, negColIdx) : null;
      const negNum = parseCellNumber(negRaw);
      if (negRaw != null && negRaw !== '' && !Number.isNaN(negNum)) target = negNum;
    }
    // Compare NUMERICALLY: a typed value round-trips through the save pipeline as a string ("5"),
    // and `"5" !== 5` would re-write forever → infinite re-render (flickering i-icons). Normalize both.
    const current = hot.getDataAtCell(row, priceColIdx);
    const currentNorm = (current === '' || current == null) ? null : parseCellNumber(current);
    const targetNorm = target === '' ? null : target;
    if (sbKey && targetNorm !== null) recordMirroredBaseline(sbKey, lotId);
    if (currentNorm !== targetNorm) hot.setDataAtCell(row, priceColIdx, target);
    // refreshKey: a flavor applied via the read-only `nsb_benchmark` column updates appliedSmartBaselineKeys
    // and fires triggerBenchmarkRefresh (bumping refreshKey) but does NOT change `value`; depend on it so
    // the mirror re-runs and the Baseline cell follows the new pick.
  }, [value, row, hotRef, reapplyKey, refreshKey, columnSettings]);

  // Manual entry (always allowed): cells are editable, so if the user types a value that isn't the
  // applied baseline price, flip THIS row to "Manual Entry". We rewrite the applied meta (label +
  // typed price) so the cell renders "Manual Entry" (keeping the matched link) and the Mirror
  // baseline follows the typed value. Numeric compare (value round-trips as a string) prevents a
  // re-write loop.
  useEffect(() => {
    if (!sbKey) return;
    const num = parseCellNumber(value);
    // A zero is a transient, not a decision: the cell reads 0 mid-rebuild after a save, before the
    // seeded price lands. Blank and null are excluded for the same reason.
    if (value === '' || value == null || Number.isNaN(num) || num === 0) return;
    const applied = appliedSmartBaselineKeys.get(sbKey);
    // A flavor the buyer picked (userSelected) flips too: the pick shields the row from event-level
    // re-seeds, not from the buyer typing over it. "Manual entry → open link → pick a flavor" still
    // lands on the flavor because the pickers record the pick before writing the cell, so the cell's
    // new value equals the applied price. The row stays userSelected, as the Intel modal's own
    // Manual Entry apply leaves it.
    if (
      !applied
      || num.toFixed(pricePrecision) === Number(applied.price).toFixed(pricePrecision)
    ) return;
    appliedSmartBaselineKeys.set(sbKey, { confScore: 0, flavorLabel: MANUAL_ENTRY_LABEL_KEY, price: num });
    // A row already on Manual Entry is being typed over again: only its price moves. Both the display
    // and the Mirror baseline read the applied price, so it must track every edit, not just the first.
    if (applied.flavorLabel !== MANUAL_ENTRY_LABEL_KEY) {
      setNsbRowConfigColumn(sbKey, 'negotiation', MANUAL_ENTRY, lotId);
      invalidateRow(sbKey);
    }
    setReapplyKey(k => k + 1);
    // pricePrecision is a dependency because the manual-entry comparison above rounds to it —
    // matches BaselinePriceRenderer's effect deps.
  }, [value, sbKey, pricePrecision]);

  // Manual Entry: this column is a plain editable price cell — show the typed value, no link / no
  // smart-baseline display. (Editing is handled by Handsontable's editor on the editable column.)
  // Intelligent Counter Offer: no smart-baseline price is seeded. The cell shows "Set after round 1"
  // until the backend fills NSP from the lowest round-1 bid; once populated it
  // shows the price plus an "Intelligent Counter Offer" label (no link, since there is no flavor to open).
  // Declared before the early returns below, because hooks must run in the same order every render.
  const openQuickPicker = useCallback((rect: DOMRect, data: NsbFlavorsResponse) => {
    if (!sbKey || !nsbItem) return;
    showSmartBaselineQuickPicker({
      anchorRect: { bottom: rect.bottom, left: rect.left, right: rect.right, top: rect.top },
      column: 'negotiation',
      // Same key as generateTableConfiguration's nsb_benchmark / negotiationStartingPrice column titles.
      columnLabel: t('line_items.negotiation_price'),
      columnSettings,
      currency,
      eventId: request?.id,
      flavors: data,
      hotRef,
      isBaseline: false,
      lotId,
      nsbItem,
      row: row ?? 0,
      sbKey,
    });
  }, [sbKey, nsbItem, columnSettings, currency, request?.id, hotRef, lotId, row, t]);

  // NSB #3/#7: the buyer uploaded this row's price. Show that price (whatever the cell currently holds —
  // the upload, or a value they've since typed) labeled "User Provided", with no link. NSB never applies
  // and never offers alternates here. The cell stays editable, so the buyer can still change the price.
  if (isUserProvided(sbKey)) {
    return (
      <UserProvidedBaselineCell
        cellRowClassName={styles.cellRow}
        currency={currency}
        labelClassName={styles.label}
        priceClassName={styles.priceText}
        pricePrecision={pricePrecision}
        value={value}
      />
    );
  }

  const icoVal = parseCellNumber(value);
  const hasIcoPrice = value !== '' && value != null && !Number.isNaN(icoVal) && icoVal > 0;
  // A row still on the backend's ICO fallback that has no match to replace it reads exactly like an
  // event-level ICO row. A matched one falls through, so the seeder's smart baseline shows instead.
  const isUnmatchedIcoFallback = (!nsbItem || !lineItemName) && isIcoFallback(sbKey) && !hasIcoPrice;
  if (getStartingPriceSelection() === INTELLIGENT_COUNTER_OFFER || isUnmatchedIcoFallback) {
    return (
      <div className={`cell-editor ${styles.cellRow}`}>
        <span className={`line-item-cell-renderer__text ${styles.priceText}`}>
          {hasIcoPrice ? displayCurrencyWithPrecision(icoVal, currency, pricePrecision) : t('line_items.set_after_round', { round: 1 })}
        </span>
        <span className={`line-item-cell-renderer__text ${styles.label}`}>
          {t('bid_request.line_items.nsb_price_selectors.method_intelligent_counter_offer')}
        </span>
      </div>
    );
  }

  if (getStartingPriceSelection() === MANUAL_ENTRY) {
    const manualVal = parseCellNumber(value);
    const showManual = value !== '' && value != null && !Number.isNaN(manualVal);
    return (
      <div className='cell-editor'>
        <span className={`line-item-cell-renderer__text ${styles.priceText}`}>
          {showManual ? displayCurrencyWithPrecision(manualVal, currency, pricePrecision) : NO_DATA_DISPLAY}
        </span>
      </div>
    );
  }

  // No resolved smart-baseline match for this row (no-match, or still resolving). The column is
  // editable, so this is a plain manual-entry cell — show the typed value if present, else a dash.
  if (!nsbItem || !lineItemName) {
    const noMatchVal = parseCellNumber(value);
    const showNoMatch = value !== '' && value != null && !Number.isNaN(noMatchVal);
    const noMatchAppliedMeta = sbKey ? appliedSmartBaselineKeys.get(sbKey) : undefined;
    // A "Build Baseline" commit (see nsb-build-baseline) applies a price to a row that by
    // definition has no nsb match, so it lands in this branch rather than the applied-flavor path
    // below. Show its own label instead of falling through to plain Manual Entry. Unlike Manual
    // Entry, this IS clickable (NSP only -- BaselinePriceRenderer's BPU label stays plain text, one
    // place to look up the derivation): it re-reads the persisted JSON from nsbRowConfigStore and
    // opens a read-only recap of the category/cluster/items/multiplier that produced this price.
    if (noMatchAppliedMeta && noMatchAppliedMeta.flavorLabel === BUILT_BASELINE_LABEL_KEY) {
      const handleShowBuiltBaselineDetails = () => {
        const details = sbKey ? parseBuiltBaselineDetails(getNsbRowConfig(sbKey)?.negotiation) : null;
        if (details) showBuiltBaselineDetailsModal(details, currency);
      };
      return (
        <div className={`cell-editor ${styles.cellRow}`}>
          <span className={`line-item-cell-renderer__text ${styles.priceText}`}>
            {displayCurrencyWithPrecision(noMatchAppliedMeta.price, currency, pricePrecision)}
          </span>
          <span
            className={`line-item-cell-renderer__text line-item-cell-renderer__link ${styles.link}`}
            onClick={handleShowBuiltBaselineDetails}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleShowBuiltBaselineDetails(); }}
            role='button'
            tabIndex={0}
          >
            {translateFlavorLabel(t, noMatchAppliedMeta.flavorLabel)}
          </span>
        </div>
      );
    }
    // A backend ICO fill lands here too, and for the same reason: ICO selects rows whose negotiation
    // starting price is zero, which are disproportionately the rows that never matched. Without this
    // the row falls through to plain Manual Entry below and credits the buyer with a price the system
    // derived. Not clickable, unlike Built Baseline — there is no derivation to open.
    if (noMatchAppliedMeta && noMatchAppliedMeta.flavorLabel === ICO_LABEL_KEY) {
      return (
        <div className={`cell-editor ${styles.cellRow}`}>
          <span className={`line-item-cell-renderer__text ${styles.priceText}`}>
            {displayCurrencyWithPrecision(noMatchAppliedMeta.price, currency, pricePrecision)}
          </span>
          <span className={`line-item-cell-renderer__text line-item-cell-renderer__link ${styles.label}`}>
            {translateFlavorLabel(t, noMatchAppliedMeta.flavorLabel)}
          </span>
        </div>
      );
    }
    // No baseline to offer → this row is plain Manual Entry: editable price + a "Manual Entry" label
    // with NO link (nothing to open).
    return (
      <div className={`cell-editor ${styles.cellRow}`}>
        <span className={`line-item-cell-renderer__text ${styles.priceText}`}>
          {showNoMatch ? displayCurrencyWithPrecision(noMatchVal, currency, pricePrecision) : NO_DATA_DISPLAY}
        </span>
        <span className={`line-item-cell-renderer__text line-item-cell-renderer__link ${styles.label}`}>{t('line_items.manual_entry')}</span>
      </div>
    );
  }

  const prec = flavors?.precomputes;
  const appliedMeta = sbKey ? appliedSmartBaselineKeys.get(sbKey) : undefined;

  // Resolve the applied (or recommended) price and its price-type label for this row.
  let price: number | null = null;
  let priceTypeLabel = '';
  if (appliedMeta) {
    price = appliedMeta.price;
    priceTypeLabel = appliedMeta.flavorLabel;
  } else if (prec) {
    const recPrice = prec.recommended_price ?? (prec.recommended_flavor === 'F3-5' ? prec.hist_avg_price : null);
    const recFlavorDef = FLAVOR_DEFS.find(f =>
      f.id === prec.recommended_flavor ||
      (prec.recommended_flavor === 'F3-5' && f.id === 'F3'));
    if (recPrice != null) {
      price = recPrice;
      priceTypeLabel = recFlavorDef?.label ?? '';
    }
  }

  const handleIntelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!sbKey || !nsbItem || !cacheKey) return;
    const data = flavors ?? getCachedFlavors(cacheKey);
    if (!data) return;
    openQuickPicker((e.currentTarget as HTMLElement).getBoundingClientRect(), data);
  };

  // No freshly-recomputed smart price yet (e.g. on reload before the flavor re-fetch completes, or if
  // it fails). Show the SAVED cell value so the page comes back exactly as it was left; fall back to a
  // dash only when there is genuinely nothing stored for this row.
  if (price == null) {
    const savedVal = parseCellNumber(value);
    const showSaved = value !== '' && value != null && !Number.isNaN(savedVal);
    let fallbackText = NO_DATA_DISPLAY;
    if (showSaved) fallbackText = displayCurrencyWithPrecision(savedVal, currency, pricePrecision);
    else if (loading) fallbackText = t('line_items.loading_baseline');
    return (
      <div className='cell-editor'>
        <span className={`line-item-cell-renderer__text ${showSaved ? styles.priceText : styles.placeholder}`}>
          {fallbackText}
        </span>
      </div>
    );
  }

  // No read-only lock: cells are ALWAYS editable so manual entry is always possible. Typing a value
  // that isn't the baseline price flips this row to "Manual Entry" (see the manual-entry effect
  // above). The override toggle governs only switching to a different smart flavor (the picker Apply).
  return (
    <div className={`cell-editor ${styles.cellRow}`}>
      <span className={`line-item-cell-renderer__text ${styles.priceText}`}>
        {displayCurrencyWithPrecision(price, currency, pricePrecision)}
      </span>
      <span
        className={`line-item-cell-renderer__text line-item-cell-renderer__link ${styles.link}`}
        onClick={handleIntelClick}
        onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') handleIntelClick(e as any); }}
        role='button'
        tabIndex={0}
      >
        {translateFlavorLabel(t, priceTypeLabel)}
      </span>
    </div>
  );
};
